import { Component, Input, OnChanges, OnDestroy, SimpleChanges, AfterViewInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatRipple } from '@angular/material/core';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmOpenComponent } from '../confirm-open/confirm-open.component';
import { APIDataResponse, APIErrorBody, APINoDataResponse, ApiErrorCode, ContactFormData, IntegrationTransaction, OpenData, UserPaymentMethods, Store, StoreStatus, StoreType, ValidationPreview } from 'src/app/interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CardInscriptionSelectionComponent } from '../card-inscription-selection/card-inscription-selection.component';
import { HttpService } from 'src/app/services/http.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { Subscription } from 'rxjs';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { LoginReiteService } from 'src/app/services/login-reite.service';
import { MultiTransactionStateManagerService } from 'src/app/services/multi-transaction-state-manager.service';
import { ConfirmDocumentInscriptionDialogComponent } from './confirm-document-inscription-dialog/confirm-document-inscription-dialog.component';
import { PaymentMethodErrorDialogComponent, StoreOpenBlockedAction } from './payment-method-error-dialog/payment-method-error-dialog.component';
import { ContactModalComponent } from 'src/app/private/pages/faq/contact-modal/contact-modal.component';
import { PaymentMethodsService } from 'src/app/services/payment-methods.service';
import { LastStoresService } from 'src/app/services/last-stores.service';
import { TranslateService } from '@ngx-translate/core';
import { MercadoPagoService } from 'src/app/services/mercadopago.service';
import { AcceptLegalGateService } from 'src/app/services/accept-legal-gate.service';

export interface ActionButton {
  label: string;
  disabled: boolean;
  action: (() => void) | null;
}

@Component({
  selector: 'app-action-button-footer',
  templateUrl: './action-button-footer.component.html',
  styleUrls: ['./action-button-footer.component.scss']
})
export class ActionButtonFooterComponent implements OnDestroy, OnChanges, AfterViewInit {

  @Input() isLogged: boolean;
  @Input() transactionsCount: number = 0;
  @Input() userPaymentMethodsForStore: UserPaymentMethods;
  @Input() requiresDocumentInscription: boolean = false;
  @Input() store: Store;
  @Input() validationPreview: ValidationPreview | null = null;
  @ViewChild(MatRipple) ripple!: MatRipple;

  storeLoaded: boolean = false;
  userPaymentMethodsLoaded: boolean = false;
  storeAcceptedPaymentMethods: {[key: string]: boolean} = {};

  subscriptions: Subscription[] = [];
  requireSecurityCode: boolean = false;
  securityCode: string | null = null;
  actionButton: ActionButton = {
    label: "",
    disabled: false,
    action: null
  };

  showGlow: boolean = false;
  turnOffGlowTimeout: any;
  rippleInterval: any;
  rippleTimeout: any;
  openCooldown: any;

  constructor(
    public dialog: MatDialog,
    private firestore: FirestoreService,
    private router: Router,
    private _snackBar: MatSnackBar,
    private analytics: AnalyticsService,
    private spinner: NgxSpinnerService,
    private http: HttpService,
    private loginReite: LoginReiteService,
    private multiTransactionStateManager: MultiTransactionStateManagerService,
    private paymentMethodsService: PaymentMethodsService,
    private lastStoresService: LastStoresService,
    private translate: TranslateService,
    private mercadoPagoService: MercadoPagoService,
    private acceptLegalGate: AcceptLegalGateService
  ) { }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    clearTimeout(this.turnOffGlowTimeout);
    clearInterval(this.rippleInterval);
    clearTimeout(this.rippleTimeout);
    clearTimeout(this.openCooldown);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ("store" in changes) {
      if (changes.store.currentValue) {
        this.storeLoaded = true;
      }
    }
    if ("userPaymentMethodsForStore" in changes) {
      if (changes.userPaymentMethodsForStore.currentValue !== undefined) {
        this.userPaymentMethodsLoaded = true;
      }
    }
    this.changeDataLoaded();
  }

  ngAfterViewInit() {
    this.rippleInterval = setInterval(() => {
      if (!this.actionButton.disabled) {
        this.triggerRipple();
      }
    }, 2000);
  }

  triggerRipple() {
    const rippleRef = this.ripple.launch({
      persistent: false, // Hace que el ripple sea persistente
      centered: true,   // Opción de centrar el ripple
      animation: {
        enterDuration: 1250, // Duración de la entrada
        exitDuration: 1250  // Duración de la salida
      }
    });
    this.rippleTimeout = setTimeout(() => {
      rippleRef.fadeOut();
    }, 4000);  // Tiempo de vida del ripple antes de desvanecerse
  }

  autoConfirm() {
    if (this.multiTransactionStateManager.previousTransactionResults !== true) {
      this.securityCode = this.multiTransactionStateManager.previousTransactionResults?.securityCode;
    }
    this.openStore();
  }

  openConfirmDialog() {
    this.analytics.logEvent('open_confirm_open_dialog');
    const dialogRef = this.dialog.open(ConfirmOpenComponent, {
      data: {
        requireSecurityCode: this.requireSecurityCode,
        securityCode: this.securityCode,
        transactionsCount: this.transactionsCount,
        store: this.store,
        validationPreview: this.validationPreview
      },
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      autoFocus: false
      });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result !== true) {
          this.securityCode = result['securityCode'];
        }
        this.multiTransactionStateManager.enableAutoconfirm(result);
        this.openStore();
      }
      else {
        this.analytics.logEvent("close_open_store_dialog")
      }
    });
  }

  goToCardInscription() {
    this.dialog.open(CardInscriptionSelectionComponent, {
      width: '90vw',
      maxWidth: '500px',
      data: {
        acceptedPaymentMethods: this.storeAcceptedPaymentMethods,
        storeId: this.store?.storeId,
        origin: "catalog"
      }
    });
  }

  openStore() {
    if (this.openCooldown) {
      return;
    }
    this.openCooldown = setTimeout(() => {
      this.clearCooldown();
    }, 1000);
    this.analytics.logEvent('open_store');
    this.spinner.show("cooler-opening");
    // Read through the service: the fingerprinting script loads on demand, so touching the global
    // bare would throw here, inside the argument list and outside any catch. The signal is
    // best-effort; the open is not.
    this.http.openStore(
      this.store?.storeId,
      this.securityCode,
      this.mercadoPagoService.getDeviceSessionId() || null
    )
    .then((openResponse: APIDataResponse<OpenData>) => {
      if (openResponse.successful) {
        let transactionId = openResponse.data.transactionId;
        let superTransactionId = openResponse.data.superTransactionId;
        let doc = this.firestore.transactionDocumentReference(transactionId, superTransactionId);
        this.subscriptions.push(doc?.snapshotChanges().subscribe({
          next: (data) => {
            if (data.payload.exists) {
              let updatedData = data.payload.data() as IntegrationTransaction;
              const storeStatus = updatedData.storeStatus;
              if (storeStatus == StoreStatus.OPENED || storeStatus == StoreStatus.BUYING) {
                this.spinner.hide("cooler-opening");
                this.lastStoresService.notifyStoreOpened(this.store);
                this.router.navigate(
                  ['message'],
                  { queryParams: { transactionId: transactionId, storeId: this.store?.storeId, superTransactionId: superTransactionId, storeType: this.store.storeType },
                    replaceUrl: true
                  }
                );
              }
              else if (storeStatus == StoreStatus.FAILED) {
                this.spinner.hide("cooler-opening");
                this._openSnackBar(this.translate.instant("components.action_button_footer.messages.store_open_failed"));
              }
            }
            else {
              this.analytics.logEvent('transaction_not_found');
              this.spinner.hide("cooler-opening");
              this._openSnackBar(this.translate.instant("components.action_button_footer.messages.transaction_create_failed"));
            }
          },
          error: (error: HttpErrorResponse) => {
            this.analytics.logEvent('open_store_error');
            this.spinner.hide("cooler-opening");
            console.log(error.error.message);
          }
        }));
      }
      else {
        this.analytics.logEvent('open_store_error');
        this.spinner.hide("cooler-opening");
        this._openSnackBar(this.translate.instant("components.action_button_footer.messages.store_communication_error"));
      }
    })
    .catch((error: HttpErrorResponse) => {
      // Branch on the body's `code`, never on its `message`: showing the backend copy for a fraud
      // code would disclose the ban or blacklist.
      const errorBody = error.error as APIErrorBody | undefined;
      const code: string | undefined = errorBody?.code ?? undefined;
      this.spinner.hide("cooler-opening");
      // A 403 only reaches the blocked-payment dialog on a recognized fraud code; every other 403
      // falls through to the snackbars below.
      const isFraudBlock403 = error.status == 403 &&
        (code === ApiErrorCode.USER_BANNED || code === ApiErrorCode.CARD_BANNED);
      if (error.status == 402 || isFraudBlock403) {
        this.analytics.logEvent('open_store_blocked', { status: error.status, code: code ?? 'none' });
        const dialogRef = this.dialog.open(PaymentMethodErrorDialogComponent, {
          panelClass: 'error-dialog',
          data: {
            code: code,
            message: errorBody?.message,
            errorCode: error.status
          },
          width: '90%',
          maxWidth: '500px',
          disableClose: false,
          autoFocus: true
        });

        dialogRef.afterClosed().subscribe((action: StoreOpenBlockedAction | undefined) => {
          if (action === 'add') {
            this.goToCardInscription();
          } else if (action === 'support') {
            this.openSupport();
          } else if (action === 'payDebt') {
            this.router.navigate(['history']);
          }
        });
      }
      else if (error.status == 403 && code === ApiErrorCode.CONSENT_REQUIRED_AT_OPEN) {
        // A routing obligation, not an error to display: this code means present the acceptance
        // gate. Reaching it means the client's consent state is behind the server's — an earlier
        // `/consents/me` failure, or a version published while the user sat here without
        // navigating.
        this.analytics.logEvent('open_store_consent_required', { code });
        this.divertToAcceptLegal();
      }
      else if (error.status == 403) {
        // Unrecognized / un-coded 403 (e.g. incomplete registration). Keep the legacy guidance.
        this.analytics.logEvent('open_store_deny_registration', { code: code ?? 'none' });
        this._openSnackBar(this.translate.instant("components.action_button_footer.messages.incomplete_registration"));
      }
      else if (error.status == 409) {
        this.analytics.logEvent('open_store_deny_pending');
        this._openSnackBar(this.translate.instant("components.action_button_footer.messages.pending_transaction"));
      }
      else if (error.status == 429) {
        // New-account open-velocity cap. Intentionally vague, no reason disclosed.
        this.analytics.logEvent('open_store_deny_velocity');
        this._openSnackBar(this.translate.instant("components.action_button_footer.messages.too_many_attempts"));
      }
      else {
        this.analytics.logEvent('open_store_error');
        this._openSnackBar(this.translate.instant("components.action_button_footer.messages.store_communication_error"));
      }
    })
  }

  /**
   * Raise the acceptance gate after the open-time consent check refused them.
   *
   * Nothing pending after the refresh means `/consents/me` is unreachable and `ConsentService` is
   * failing open, so there is no gate to raise. That gets its own message: a user can act on "try
   * again in a moment", not on advice to finish a registration that is already complete.
   */
  private async divertToAcceptLegal(): Promise<void> {
    if (await this.acceptLegalGate.presentAfterServerRefusal()) return;
    this.analytics.logEvent('open_store_consent_required_unresolved');
    this._openSnackBar(this.translate.instant("components.action_button_footer.messages.consent_unavailable"));
  }

  /**
   * The CTA for blocks the user cannot self-resolve (banned account, linked-account debt). The
   * underlying reason is never disclosed.
   */
  openSupport(): void {
    this.analytics.logEvent('open_store_blocked_contact_support');
    const dialogRef = this.dialog.open(ContactModalComponent, {
      data: { isLogged: true },
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      autoFocus: false
    });
    dialogRef.afterClosed().subscribe((formData: ContactFormData) => {
      if (!formData) return;
      this.spinner.show();
      const helpPromise: Promise<APINoDataResponse> = formData.transactionId
        ? this.http.transactionsHelp(formData.transactionId, formData)
        : this.http.usersHelp(formData);
      helpPromise
        .then((response: APINoDataResponse) => {
          this._openSnackBar(this.translate.instant(response.successful
            ? "components.action_button_footer.messages.support_message_sent"
            : "components.action_button_footer.messages.support_message_error"));
        })
        .catch(() => {
          this._openSnackBar(this.translate.instant("components.action_button_footer.messages.support_message_error"));
        })
        .finally(() => this.spinner.hide());
    });
  }

  goToLogin() {
    this.analytics.logEvent('redirect_to_login');
    this.loginReite.openPopup();
    this.router.navigate(['login'], { queryParams: { storeId: this.store?.storeId } });
  }

  goToDocumentInscription(): void {
    this.dialog.open(ConfirmDocumentInscriptionDialogComponent).afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigateByUrl(`document-inscription/${this.store?.storeId}`);
      }
    })
  }

  _openSnackBar(message: string) {
    let action = this.translate.instant("common.close")
    this._snackBar.open(message, action, {
      duration: 10000,
    });
  }


  changeDataLoaded() {
    if (this.storeLoaded && this.userPaymentMethodsLoaded) {
      if (this.isLogged) {
        // Use service to normalize store accepted payment methods
        this.storeAcceptedPaymentMethods = this.paymentMethodsService.getStoreAcceptedPaymentMethodsMap(
          this.store?.acceptedPaymentMethods || {}
        );
      }

      if (!this.isLogged) {
        this.actionButton = {
          label: this.translate.instant("components.action_button_footer.labels.log_in"),
          disabled: false,
          action: () => this.goToLogin()
        };
        return;
      }
      if (!this.store?.active) {
        this.actionButton = {
          label: this.translate.instant("components.action_button_footer.labels.store_disabled"),
          disabled: true,
          action: null
        }
        return;
      }
      if (this.requiresDocumentInscription) {
        this.actionButton = {
          label: this.translate.instant("components.action_button_footer.labels.inscribe_document"),
          disabled: false,
          action: () => this.goToDocumentInscription()
        }
        return;
      }
      // Use service to check if user has valid payment methods for this store
      if (this.paymentMethodsService.hasValidPaymentMethodForStore(this.store?.storeId)) {
        let storeType = this.store?.storeType || StoreType.SMART;
        let label = this.translate.instant(storeType != StoreType.COFFEE
          ? "components.action_button_footer.labels.open_store"
          : "components.action_button_footer.labels.connect_store");
        this.actionButton = {
          label: label,
          disabled: false,
          action: () => {
            if (this.multiTransactionStateManager.autoConfirmEnabled) {
              this.autoConfirm();
            }
            else {
              this.openConfirmDialog();
            }
          }
        }
        return;
      }
      else if (Object.keys(this.storeAcceptedPaymentMethods).length > 0) {
        this.actionButton = {
          label: this.translate.instant("components.action_button_footer.labels.add_payment_method"),
          disabled: false,
          action: () => this.goToCardInscription()
        }
        return;
      }
      else {
        this.actionButton = {
          label: this.translate.instant("components.action_button_footer.labels.no_payment_method"),
          disabled: true,
          action: null
        };
        return;
      }
    }
  }

  clearCooldown() {
    clearTimeout(this.openCooldown);
    this.openCooldown = null;
  }

  activateGlowEffect() {
    clearTimeout(this.turnOffGlowTimeout);
    this.showGlow = true;
    this.turnOffGlowTimeout = setTimeout(() => {
      this.showGlow = false;
    }, 3000);
  }
}
